/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/ban-types */
import React, { useMemo } from 'react';
import { useConfig, useDesktop, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { createContext, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { KazooUser, KazooUserCredentials, VoiceApi } from '../../services/api-client/voice-api';
import { w3cwebsocket } from 'websocket';
import { ChannelMessage } from './kazoo-wss-types';
import { ChannelEvent, ChannelListener, IKazooContext, UserInfoStatus } from './types';

let kazooClient: w3cwebsocket = null!;
const credentialsRefreshMilliseconds = 45 * 60 * 1000;

export const KazooContext = createContext<IKazooContext>(null!);

export default function KazooProvider({ children }: PropsWithChildren<{}>) {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { isDesktopApplication, isDesktopWidget } = useDesktop();

  const [userCredentials, setUserCredentials] = useState<KazooUserCredentials>(null!);
  const [userInfo, setUserInfo] = useState<KazooUser>(null!);
  const [userInfoStatus, setUserInfoStatus] = useState<UserInfoStatus>('default');
  const channelListeners = useRef(new Set<ChannelListener>());

  const notifyChannelListeners = (event: ChannelEvent) => (message: ChannelMessage) =>
    channelListeners.current.forEach((listener) => listener(event, message));

  const messageHandlerMap: Record<string, any> = {
    'call.CHANNEL_CREATE.*': notifyChannelListeners('create'),
    'call.CHANNEL_DESTROY.*': notifyChannelListeners('destroy')
  };

  const onChannelMessage = useCallback((listener: ChannelListener) => {
    channelListeners.current.add(listener);
    return () => {
      channelListeners.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!(config && oidcService)) return;
    if (isDesktopApplication() && !isDesktopWidget) return;

    (async () => {
      try {
        setUserInfoStatus('pending');
        const api = new VoiceApi(config, oidcService);
        const [kazooUser, credentials] = await Promise.all([api.getUserInfo(), api.getKazooUserCredentials()]);
        setUserInfo(kazooUser);
        setUserCredentials(credentials);
        setUserInfoStatus('success');
      } catch {
        setUserInfoStatus('error');
      }
    })();
  }, []);

  useEffect(() => {
    const handlePageLeave = () => deinitialize();
    window.addEventListener('unload', handlePageLeave);
    window.addEventListener('pagehide', handlePageLeave);
    const refreshInterval = setInterval(() => refreshUserCredentials(), credentialsRefreshMilliseconds);

    return () => {
      deinitialize();
      window.removeEventListener('unload', handlePageLeave);
      window.removeEventListener('pagehide', handlePageLeave);
      clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (userCredentials) {
        if (isStarted()) await deinitialize();
        await connect();
      } else {
        await deinitialize();
      }
    })();
  }, [userCredentials]);

  const isStarted = () => !!kazooClient;

  const connect = async () => {
    if (userCredentials) {
      kazooClient = new w3cwebsocket(`${userCredentials.kazooApiWssUrl}/v2/websockets`);

      kazooClient.onopen = async () => {
        const promises = Object.keys(messageHandlerMap).map((key) => subscribe(key));
        await Promise.all(promises);
      };

      kazooClient.onmessage = (rawMessage: any) => {
        const jsonData = JSON.parse(rawMessage.data);
        if (jsonData.subscribed_key) handleMessage(jsonData);
      };

      const reconnect = async () => {
        if (kazooClient) {
          await deinitialize();
          connect();
        }
      };

      kazooClient.onclose = (e) => {
        if (e.code === 1006) reconnect();
      };
    }
  };

  const handleMessage = async (message: ChannelMessage) => {
    const handler = messageHandlerMap[message.subscribed_key];
    if (handler) handler(message);
  };

  const subscribe = async (binding: string) => {
    if (userCredentials) {
      const data = {
        action: 'subscribe',
        auth_token: userCredentials.kazooUserToken,
        data: {
          account_id: userCredentials.kazooAccountId,
          binding
        }
      };
      kazooClient?.send(JSON.stringify(data));
    }
  };

  const deinitialize = async () => {
    if (kazooClient) {
      try {
        const client = kazooClient;
        kazooClient = null!;
        client.close();
      } catch (ex) {
        console.error('ex : ', ex);
      }
    }
  };

  const refreshUserCredentials = async () => {
    const credentials = await new VoiceApi(config, oidcService).getKazooUserCredentials();
    setUserCredentials(credentials);
  };

  const providerValue = useMemo(
    () => ({
      userCredentials,
      userInfo,
      userInfoStatus,
      onChannelMessage
    }),
    [userCredentials, userInfo, userInfoStatus, onChannelMessage]
  );

    //@ts-ignore
  return <KazooContext.Provider value={providerValue}>{children}</KazooContext.Provider>;
}
